import type { ComponentProps, ReactNode } from 'react';
import { forwardRef, useRef } from 'react';
import type AvatarEditor from 'react-avatar-editor';

import { ImageUploadSuccessModal } from './modals/ImageUploadSuccessModal';
import { ImageUploadDropdown } from './components/ImageUploadDropdown';
import { ImageUploadErrorModal } from './modals/ImageUploadErrorModal';
import { ImageRemoveModal } from './modals/ImageRemoveModal';
import { ImageRemoveSuccessModal } from './modals/ImageRemoveSuccessModal';
import { ImageLoadingModal } from './modals/ImageLoadingModal';
import { ImageUploadResizeModal } from './modals/ImageUploadResizeModal';
import { ImageUploadProvider } from './ImageUploadContext';
import { ImageUploadModal } from './ImageUploadService';
import { ImageUploadInput } from './components/ImageUploadInput';

type Props = {
    dropdownToggle?: ReactNode;
    onChangeImageClick?: () => void;
    acceptedImageTypes?: string[];
} & ComponentProps<typeof ImageRemoveSuccessModal> &
    Pick<
        ComponentProps<typeof ImageUploadResizeModal>,
        'onUploadedImageChange' | 'onImageUpload' | 'onImageDataUrlChange'
    > &
    Pick<ComponentProps<typeof ImageUploadDropdown>, 'disableRemoveImage' | 'onDropdownOpen'> &
    Pick<ComponentProps<typeof ImageRemoveModal>, 'onImageRemove'>;

export const ImageUpload = forwardRef<HTMLInputElement, Props>(
    (
        {
            onUploadedImageChange,
            onImageDataUrlChange,
            showResultAsNotification,
            onImageRemove,
            disableRemoveImage,
            dropdownToggle,
            onImageUpload,
            onDropdownOpen,
            onChangeImageClick,
            acceptedImageTypes,
        },
        ref,
    ) => {
        const avatarEditor = useRef<AvatarEditor>(null);
        const inputRef = useRef<HTMLInputElement>(null);

        const handleUploadClick = () => {
            onChangeImageClick?.();
            inputRef.current?.click();
        };

        return (
            <ImageUploadProvider value={{ currentModal: ImageUploadModal.None, imageUrl: '' }}>
                <ImageUploadInput ref={ref || inputRef} acceptedImageTypes={acceptedImageTypes} />
                {dropdownToggle && (
                    <ImageUploadDropdown
                        disableRemoveImage={disableRemoveImage}
                        dropdownToggle={dropdownToggle}
                        onDropdownOpen={onDropdownOpen}
                        onUploadClick={handleUploadClick}
                    />
                )}
                <ImageLoadingModal />
                <ImageUploadResizeModal
                    ref={avatarEditor}
                    onUploadedImageChange={onUploadedImageChange}
                    onImageDataUrlChange={onImageDataUrlChange}
                    avatarEditor={avatarEditor}
                    onImageUpload={onImageUpload}
                    showResultAsNotification={showResultAsNotification}
                />
                <ImageRemoveModal
                    onUploadedImageChange={onUploadedImageChange}
                    onImageDataUrlChange={onImageDataUrlChange}
                    onImageRemove={onImageRemove}
                />
                <ImageUploadSuccessModal />
                <ImageRemoveSuccessModal showResultAsNotification={showResultAsNotification} />
                <ImageUploadErrorModal showResultAsNotification={showResultAsNotification} />
            </ImageUploadProvider>
        );
    },
);
